import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { 
  ArrowLeft, 
  ChevronDown, 
  AlertTriangle,
  CheckCircle2,
  HelpCircle,
  FileText,
  FileSpreadsheet,
  Zap,
  ExternalLink,
  Copy,
  Check,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import type { RiskLevel } from "@/lib/types";
import { useRunResults, getRiskLevelFromScore, getVerdictFromScore } from "@/hooks/useRunResults";

const severityColors: Record<string, string> = {
  low: "bg-blue-500/20 text-blue-400 border-blue-500/30",
  medium: "bg-reqqy-warning/20 text-reqqy-warning border-reqqy-warning/30",
  high: "bg-destructive/20 text-destructive border-destructive/30",
};

const riskColors: Record<RiskLevel, string> = {
  low: "bg-reqqy-success/20 text-reqqy-success",
  medium: "bg-reqqy-warning/20 text-reqqy-warning",
  high: "bg-destructive/20 text-destructive",
};

export default function RunResults() {
  const { runId } = useParams<{ runId: string }>();
  const [showOriginal, setShowOriginal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: run, isLoading, error } = useRunResults(runId);

  const getClarityColor = (score: number) => {
    if (score >= 80) return "text-reqqy-success";
    if (score >= 60) return "text-reqqy-warning";
    return "text-destructive";
  };

  const handleCopy = async (text: string, id: string) => {
    await navigator.clipboard.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleExport = (format: "markdown" | "csv") => {
    const filename = `Reqqy-clarity-report-${runId}.${format === "markdown" ? "md" : "csv"}`;
    console.log(`Downloading ${filename}`);
  };

  const handleExportToJira = () => {
    console.log("Exporting to Jira...");
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading results...</p>
        </div>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Run Not Found</h2>
          <p className="text-muted-foreground mb-4">The requested run could not be found.</p>
          <Button asChild>
            <Link to="/dashboard">Back to Dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  const results = run.result_payload;
  const clarityScore = Number(run.clarity_score) || results?.claritySummary?.score || 0;
  const riskLevel = results?.claritySummary?.riskLevel ?? getRiskLevelFromScore(clarityScore);
  const verdict = results?.claritySummary?.verdict ?? getVerdictFromScore(clarityScore);
  const issues = results?.issues ?? [];
  const improved = results?.improved ?? [];
  const clarifyingQuestions = results?.clarifyingQuestions ?? [];
  const inputPayload = run.input_payload as Record<string, unknown>;
  const originalInput = results?.original ?? (inputPayload?.raw_input as string) ?? "";

  return (
    <div className="min-h-screen p-6 md:p-8 lg:p-10">
      {/* Header */}
      <div className="mb-8">
        <Link
          to="/dashboard"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="text-2xl md:text-3xl font-display font-bold">Clarity Report</h1>
              <Badge className="bg-reqqy-success/20 text-reqqy-success hover:bg-reqqy-success/20">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Complete
              </Badge>
            </div>
            <p className="text-muted-foreground">Analysis completed • {issues.length} issues found</p>
          </div>
          <div className="flex gap-3 flex-wrap">
            <Button variant="outline" onClick={() => handleExport("markdown")}>
              <FileText className="w-4 h-4 mr-2" />
              Markdown
            </Button>
            <Button variant="outline" onClick={() => handleExport("csv")}>
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              CSV
            </Button>
            <Button variant="outline" onClick={handleExportToJira}>
              <ExternalLink className="w-4 h-4 mr-2" />
              Export to Jira
            </Button>
          </div>
        </div>
      </div>

      {/* Clarity Summary */}
      <Card className="glass mb-6">
        <CardContent className="p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex items-center gap-6">
              <div className="relative">
                <div className={cn(
                  "w-24 h-24 rounded-2xl flex items-center justify-center",
                  clarityScore >= 80 ? "bg-reqqy-success/20" :
                  clarityScore >= 60 ? "bg-reqqy-warning/20" : "bg-destructive/20"
                )}>
                  <div className="text-center">
                    <div className={cn("text-3xl font-display font-bold", getClarityColor(clarityScore))}>
                      {clarityScore}
                    </div>
                    <div className="text-xs text-muted-foreground">Clarity</div>
                  </div>
                </div>
              </div>
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Badge className={riskColors[riskLevel]}>
                    {riskLevel.charAt(0).toUpperCase() + riskLevel.slice(1)} Risk
                  </Badge>
                </div>
                <p className="text-muted-foreground max-w-md">{verdict}</p>
              </div>
            </div>
            <div className="flex-1" />
            <div className="flex gap-4 text-center">
              <div>
                <div className="text-2xl font-bold">{issues.length}</div>
                <div className="text-xs text-muted-foreground">Issues</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{clarifyingQuestions.length}</div>
                <div className="text-xs text-muted-foreground">Questions</div>
              </div>
              <div>
                <div className="text-2xl font-bold">{improved.length}</div>
                <div className="text-xs text-muted-foreground">Improved</div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Issues */}
      {issues.length > 0 && (
        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-reqqy-warning" />
              What's Unclear
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {issues.map((issue) => (
              <div
                key={issue.id}
                className={cn("p-4 rounded-xl border", severityColors[issue.severity] || severityColors.medium)}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-xs capitalize">
                      {issue.type.replace(/_/g, " ")}
                    </Badge>
                    <Badge variant="outline" className={cn("text-xs", severityColors[issue.severity] || severityColors.medium)}>
                      {issue.severity}
                    </Badge>
                  </div>
                </div>
                {issue.originalText && (
                  <p className="text-sm mb-2 font-mono bg-background/50 p-2 rounded">
                    "{issue.originalText}"
                  </p>
                )}
                <p className="text-sm">
                  <strong className="text-foreground">"{issue.text}"</strong> — {issue.explanation}
                </p>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Clarifying Questions */}
      {clarifyingQuestions.length > 0 && (
        <Card className="glass mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-primary" />
              Questions Engineers Will Ask
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-3">
              {clarifyingQuestions.map((question, index) => (
                <li key={index} className="flex items-start gap-3 p-3 rounded-lg bg-background/50">
                  <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 text-xs font-medium text-primary">
                    {index + 1}
                  </span>
                  <span className="text-sm">{question}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Improved Requirements */}
      {improved.length > 0 && (
        <Card className="glass mb-6">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Zap className="w-5 h-5 text-primary" />
              Improved Requirements
            </CardTitle>
            <Button size="sm" className="gradient-primary text-primary-foreground">
              <Check className="w-4 h-4 mr-2" />
              Accept All Improvements
            </Button>
          </CardHeader>
          <CardContent className="space-y-6">
            {improved.map((item) => (
              <div key={item.id} className="p-4 rounded-xl bg-background/50 border border-border">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <p className="text-sm text-muted-foreground mb-2">Original:</p>
                    <p className="text-sm font-mono bg-muted/50 p-2 rounded line-through opacity-60">
                      {item.original}
                    </p>
                  </div>
                </div>
                
                <div className="mb-4">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm text-muted-foreground">Improved:</p>
                    <Button variant="ghost" size="sm" onClick={() => handleCopy(item.improved, item.id)}>
                      {copiedId === item.id ? (
                        <Check className="w-4 h-4 text-reqqy-success" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                  <p className="text-sm p-3 rounded-lg bg-primary/10 border border-primary/30">
                    {item.improved}
                  </p>
                </div>

                <div className="space-y-3">
                  {item.acceptanceCriteria?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Acceptance Criteria:</p>
                      <ul className="space-y-1">
                        {item.acceptanceCriteria.map((criteria, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <CheckCircle2 className="w-4 h-4 text-reqqy-success mt-0.5 flex-shrink-0" />
                            {criteria}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.assumptions?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">Assumptions Made:</p>
                      <ul className="space-y-1">
                        {item.assumptions.map((assumption, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                            <span className="text-primary">•</span>
                            {assumption}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {item.changes?.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground mb-2">What Changed:</p>
                      <div className="flex flex-wrap gap-2">
                        {item.changes.map((change, i) => (
                          <Badge key={i} variant="outline" className="text-xs">
                            {change}
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Original Input */}
      <Card className="glass mb-6">
        <Collapsible open={showOriginal} onOpenChange={setShowOriginal}>
          <CollapsibleTrigger asChild>
            <CardHeader className="cursor-pointer hover:bg-muted/30 transition-colors">
              <CardTitle className="text-lg flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-muted-foreground" />
                  Original Input
                </span>
                <ChevronDown className={cn(
                  "w-5 h-5 text-muted-foreground transition-transform",
                  showOriginal && "rotate-180"
                )} />
              </CardTitle>
            </CardHeader>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <CardContent>
              <pre className="text-sm font-mono whitespace-pre-wrap p-4 rounded-lg bg-background/50 border border-border">
                {originalInput}
              </pre>
            </CardContent>
          </CollapsibleContent>
        </Collapsible>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-center gap-4">
        <Button variant="outline" asChild>
          <Link to="/dashboard">Back to Dashboard</Link>
        </Button>
        <Button className="gradient-primary text-primary-foreground">
          <Zap className="w-4 h-4 mr-2" />
          Go Deeper
        </Button>
      </div>
    </div>
  );
}
